/**
 * Gasless swap service (LibertySwap relayer flow).
 *
 * Port of liberty-swap's `useSwapGasless` mechanics, restructured as pure
 * helpers so both extension surfaces (popup + mobile-web) can drive the flow
 * from a shared hook.
 *
 * The user only SIGNS; a relayer pays gas and broadcasts. Two permit standards:
 *   - EIP-3009 (transferWithAuthorization): a single signature.
 *   - EIP-2612 (permit): two signatures — the ERC-20 permit, then a LBSGasless
 *     SwapParams signature that tells the relayer what to swap into.
 *
 * Reference (working impl):
 *   ~/Work/ZKX/liberty-swap/apps/liberty-swap/src/app/libs/hooks/swap/useSwapGasless.ts
 *   ~/Work/ZKX/liberty-swap/apps/liberty-swap/src/services/builder/signType/*.builder.ts
 *   ~/Work/ZKX/liberty-swap/apps/liberty-swap/src/types/signType.ts
 *
 * The relayer contract address (liberty-swap's NEXT_PUBLIC_GASLESS_V2_CONTRACT)
 * is the `spender`/`to` in the permit and the `verifyingContract` of the
 * SwapParams EIP-712 domain, so it has to match the environment the rest of the
 * stack points at. Both environments' addresses are in
 * GASLESS_V2_RELAYER_CONTRACTS below; every build helper throws rather than
 * signing against an unknown one.
 */
import { AbiCoder, hexlify, keccak256, parseUnits, Signature, toUtf8Bytes, ZeroHash } from 'ethers'

import {
  getLibertyApiEnv,
  getLibertyAppApiBase,
  getLibertyGaslessApiBase,
  LibertyApiEnv
} from './apiBase'
import { getLoadedSwapContractAddress, LIBERTY_TOKENS, setDynamicSwapContracts } from './constants'
import { getDefaultIntegratorId } from './integrator'

// ───────────────────────────────────────────────────────────── endpoints ────

// The gasless relayer namespace (`GASLESS_API_URL`). Distinct from the app/quote
// base and from the DEX API, and on v3 it is no longer a sub-path of the same
// root — hence its own base rather than a string concat.
export const getGaslessApiBase = getLibertyGaslessApiBase

// The LBSGasless v2 relayer contract (NEXT_PUBLIC_GASLESS_V2_CONTRACT upstream,
// i.e. per-environment there too). One address across chains; both are deployed
// on every supported chain with identical bytecode.
//
// This MUST follow the same env switch as the API host. It is the EIP-3009
// `to` and is therefore covered by the user's signature, while the relayer
// submits `transferWithAuthorization` with ITS OWN contract as `to`. Point the
// stack at dev while signing the prod address and the token contract rejects the
// authorization with "FiatTokenV2: invalid signature" — the swap dies after the
// user has already signed, with nothing on-chain to show for it.
//
// Guessing either value is not harmless for the same reason: a wrong address
// produces a perfectly valid signature that the relayer can only reject. Two
// candidates were tried against the dev relayer before the real one was known
// and both were rejected — the production address, and the "staging" one noted
// in this file's history (0xBeF80925da31903ea9e01d3115FaFE4B3b6830DC).
//
// The dev address below is the value liberty-swap itself ships as
// NEXT_PUBLIC_GASLESS_V2_CONTRACT for its dev environment, confirmed working
// against the dev relayer on 2026-08-17. It lives here rather than only in
// `.env` because `.env` is gitignored: a deployed dev build gets whatever the
// build host happened to export, and gasless silently turned off on
// app.dev.zkxwallet.com exactly that way. `setGaslessV2Contract` still
// overrides it when a build needs to point somewhere else. Public on-chain
// addresses, not secrets.
export const GASLESS_V2_RELAYER_CONTRACTS: Record<LibertyApiEnv, string> = {
  dev: '0x4c1b38eB439bdDEc1d40C9cb749BFFCBcaB5d541',
  // Production value supplied by the LibertySwap team on 2026-08-19
  // (NEXT_PUBLIC_GASLESS_V2_CONTRACT). It replaces
  // 0xcf3D89AedD07eE94E5c45037581744e2d9F0b9Fc, which this file had carried as
  // "production" since the migration plan but which was never confirmed by them
  // — and which the dev relayer rejected when it was tried as a candidate.
  // uat mirrors prod: a UAT build is a release candidate on the production
  // backend, so it must sign against the production contract.
  uat: '0x63d20790087845ffA370a1DF10E2CA832F93059C',
  prod: '0x63d20790087845ffA370a1DF10E2CA832F93059C'
}

let gaslessV2ContractOverride: string | null = null

/**
 * Override the gasless relayer contract for the current environment.
 *
 * This module is env-agnostic (submodule rule), so the app reads the value and
 * pushes it in at boot — same shape as `setLibertyApiEnv`. Pass null/empty to
 * fall back to the built-in table. The caller decides whether an override is
 * allowed at all; it must never be honoured on a production build, where the
 * known-good address belongs in the table above.
 */
export function setGaslessV2Contract(address: string | null | undefined): void {
  const trimmed = typeof address === 'string' ? address.trim() : ''
  gaslessV2ContractOverride = trimmed || null
}

/** The gasless relayer contract for the environment the stack is pointed at. */
export function getGaslessV2Contract(): string {
  return gaslessV2ContractOverride || GASLESS_V2_RELAYER_CONTRACTS[getLibertyApiEnv()]
}

function assertRelayerConfigured(): string {
  const env = getLibertyApiEnv()
  const contract = getGaslessV2Contract()
  if (!contract) {
    // Thrown while BUILDING the typed data, i.e. before the signature prompt —
    // the whole point is that the user never signs something unusable.
    throw new Error(
      `Gasless is not available on the "${env}" LibertySwap backend: the LBSGasless v2 ` +
        'contract address for that environment is unknown. Set ' +
        'NEXT_PUBLIC_GASLESS_V2_CONTRACT in extension/.env and restart the dev server, ' +
        'or use the production backend to test gasless.'
    )
  }
  return contract
}

// ────────────────────────────────────────────────── railgun shield (Fast) ────
// "Fast shield" reuses this exact gasless relayer flow but delivers into a
// private RAILGUN balance instead of an EVM address. Mirrors liberty-swap's
// shield mode (apps/liberty-swap: populateTransactionV2 with `isRailgun` +
// encodeRailgunAddress), which — for gasless-eligible inputs — runs through the
// same gasless executor. Three swap fields change vs a normal gasless swap:
//   - destChainId  → a RAILGUN sentinel (per liberty-swap getCustomChainId)
//   - recipient    → the server-ENCRYPTED 0zk address, utf8→hex bytes
//   - recipientType→ CUSTOM (2)
// These three are bound together in the EIP-3009 nonce hash and the submit body,
// so callers pass all three via the `railgunShield` override below and every
// builder applies them consistently.

// LibertySwap's RAILGUN sentinel destination chain ids (getCustomChainId with
// isRailgun=true). The relayer reads these to route the swap output into the
// RAILGUN shield on the matching real chain instead of a normal cross-chain
// delivery. RAILGUN is supported on Ethereum / BNB / Arbitrum only.
export const RAILGUN_SHIELD_DEST_CHAIN_IDS: Record<number, number> = {
  1: 69696969, // Ethereum
  56: 96969696, // BNB Chain
  42161: 66669999 // Arbitrum
}

/** True when a RAILGUN Fast-shield can settle on this output chain. */
export function isRailgunShieldChainSupported(outputChainId: number): boolean {
  return outputChainId in RAILGUN_SHIELD_DEST_CHAIN_IDS
}

/**
 * Map a real output chain id to its RAILGUN shield sentinel. Throws for chains
 * without RAILGUN support so a mis-scoped Fast shield fails loudly rather than
 * silently encoding a normal cross-chain swap.
 */
export function getRailgunShieldDestChainId(outputChainId: number): number {
  const sentinel = RAILGUN_SHIELD_DEST_CHAIN_IDS[outputChainId]
  if (!sentinel) {
    throw new Error(`RAILGUN Fast-shield is not supported on chain ${outputChainId}`)
  }
  return sentinel
}

// The address-encryption endpoint (liberty-swap mainAxiosInstance
// /app/addresses/encrypt). It returns an opaque string the relayer can decrypt
// back to the 0zk recipient; the swap contract carries it as utf8 bytes. Served
// under the same host as the other liberty endpoints this service already uses.
const getEncryptAddressApiUrl = () => `${getLibertyAppApiBase()}/addresses/encrypt`

/**
 * Consistent override applied by all three gasless builders to turn a normal
 * gasless swap into a RAILGUN Fast-shield.
 */
export interface RailgunShieldOverride {
  /** Server-encrypted 0zk recipient, already utf8→hex encoded (see below). */
  encryptedRecipient: string
  /** RAILGUN sentinel dest-chain id (from getRailgunShieldDestChainId). */
  destChainId: number
}

/**
 * Encrypt a 0zk RAILGUN address for the swap `recipient` bytes. Mirrors
 * liberty-swap encodeRailgunAddress(toHex=true): POST the plaintext 0zk to
 * /app/addresses/encrypt, then utf8-encode the returned string to hex bytes so
 * it matches the on-chain `bytes recipient` and the relayer's decrypt input.
 * Returns null on any failure so callers can surface a clear error instead of
 * signing a swap with a malformed recipient.
 */
export async function encryptRailgunShieldRecipient(zkAddress: string): Promise<string | null> {
  try {
    const res = await fetch(getEncryptAddressApiUrl(), {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'railgun', address: zkAddress })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || `Address encrypt failed (${res.status})`)
    }
    const json = await res.json()
    const encrypted: string | undefined = json?.data ?? json?.encrypted ?? json?.result
    if (!encrypted || typeof encrypted !== 'string') {
      throw new Error('Address encrypt returned an unexpected shape')
    }
    // utf8 bytes → hex, matching liberty encodeRailgunAddress(toHex=true).
    return hexlify(toUtf8Bytes(encrypted))
  } catch (e) {
    console.error('[RailgunShield] address encrypt failed:', e)
    return null
  }
}

// ─────────────────────────────────────────────────── token eligibility ────

// Mirrors liberty-swap GASLESS_AVAILABLE_TOKEN_IDS, using this repo's allTokens
// token-id convention (USDC chain-1 = USDC-ETH; DAI chain-1 = DAI-ETHEREUM).
// liberty-swap dropped USDC-ARBITRUM / USDC-POLYGON here when those tokens moved
// to the CCTP hook path, because its swap dispatch checks gasless BEFORE CCTP and
// would otherwise never reach the CCTP branch. This repo picks the path from an
// explicit user toggle instead, so the ordering argument does not apply — and
// USDC-ARBITRUM is a RAILGUN input whose Fast shield runs through this same list.
// They stay until the relayer confirms it no longer sponsors them.
export const GASLESS_AVAILABLE_TOKEN_IDS: string[] = [
  'USDC-BASE',
  'USDC-ARBITRUM',
  'USDC-PULSECHAIN',
  'USDC-ETH',
  'DAI-ETHEREUM',
  'USDC-POLYGON',
  'USD1-BSC',
  'USDG-ROBINHOOD'
]

export function isGaslessAvailable(tokenId: string): boolean {
  return GASLESS_AVAILABLE_TOKEN_IDS.includes(tokenId)
}

export type GaslessPermitType = 'EIP-2612' | 'EIP-3009'

// EIP-3009 tokens sign a single transferWithAuthorization; everything else uses
// the two-signature EIP-2612 permit flow. Mirrors liberty-swap getSwapPermitType.
export function getSwapPermitType(tokenId: string): GaslessPermitType {
  switch (tokenId) {
    case 'USDC-BASE':
    case 'USDC-ETH':
    case 'USDC-ARBITRUM':
    case 'USDC-POLYGON':
    case 'USDC-MONAD':
      return 'EIP-3009'
    case 'USDC-PULSECHAIN':
    case 'DAI-ETHEREUM':
    case 'USD1-BSC':
    case 'USDG-ROBINHOOD':
    default:
      return 'EIP-2612'
  }
}

// Recipient address kinds the relayer understands (see liberty-swap SwapRecipientType).
export enum SwapRecipientType {
  EVM = 0,
  SVM = 1,
  CUSTOM = 2
}

export function getSwapRecipientType(destinationAddress: string): SwapRecipientType {
  if (destinationAddress.startsWith('lbs')) return SwapRecipientType.CUSTOM
  if (destinationAddress.startsWith('0x')) return SwapRecipientType.EVM
  // Solana / other non-EVM destinations are not yet supported on this surface.
  return SwapRecipientType.CUSTOM
}

// ─────────────────────────────────────────────────────── EIP-712 types ────

// All gasless domains carry name/version/chainId/verifyingContract. The ZKX
// signer (getEIP712Signature in libs/signMessage) rejects typed data whose
// `types` omits an explicit EIP712Domain entry, so every table below includes
// it. It's EIP-712 metadata only — the relayer's hash is built from the
// domainSeparator + the primaryType struct, so listing it doesn't change the
// produced signature. Mirrors PERMIT_WITNESS_TYPES in permitWitness.ts.
const EIP712_DOMAIN_TYPE = [
  { name: 'name', type: 'string' },
  { name: 'version', type: 'string' },
  { name: 'chainId', type: 'uint256' },
  { name: 'verifyingContract', type: 'address' }
]

export const TRANSFER_WITH_AUTHORIZATION_TYPES = {
  EIP712Domain: EIP712_DOMAIN_TYPE,
  TransferWithAuthorization: [
    { name: 'from', type: 'address' },
    { name: 'to', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'validAfter', type: 'uint256' },
    { name: 'validBefore', type: 'uint256' },
    { name: 'nonce', type: 'bytes32' }
  ]
}

export const PERMIT_OWNER_TYPES = {
  EIP712Domain: EIP712_DOMAIN_TYPE,
  Permit: [
    { name: 'owner', type: 'address' },
    { name: 'spender', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' }
  ]
}

export const PERMIT_HOLDER_TYPES = {
  EIP712Domain: EIP712_DOMAIN_TYPE,
  Permit: [
    { name: 'holder', type: 'address' },
    { name: 'spender', type: 'address' },
    { name: 'nonce', type: 'uint256' },
    { name: 'expiry', type: 'uint256' },
    { name: 'allowed', type: 'bool' }
  ]
}

export const SWAP_PARAMS_TYPES = {
  EIP712Domain: EIP712_DOMAIN_TYPE,
  SwapParams: [
    { name: 'user', type: 'address' },
    { name: 'token', type: 'address' },
    { name: 'router', type: 'address' },
    { name: 'amount', type: 'uint256' },
    { name: 'destChainId', type: 'uint256' },
    { name: 'recipient', type: 'bytes' },
    { name: 'recipientType', type: 'uint8' },
    { name: 'targetToken', type: 'address' },
    // The relayer expects `integratorId` right after `targetToken` — reordering
    // it recovers a different signer and the swap silently fails.
    { name: 'integratorId', type: 'bytes32' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
    { name: 'correlationId', type: 'bytes32' }
  ]
}

export interface TypedDataDomain {
  name: string
  version?: string
  chainId: number
  verifyingContract: string
}

export interface GaslessTypedData {
  domain: TypedDataDomain
  types: Record<string, { name: string; type: string }[]>
  primaryType: string
  message: Record<string, any>
}

// ERC-20 permit (EIP-2612) domain name/version differ per token. Mirrors
// liberty-swap PermitBuilder.getDomainInfo (its keys carried a typo for
// PulseChain; corrected here).
function getPermitDomainInfo(tokenId: string): { name: string; version: string } {
  switch (tokenId) {
    case 'DAI-ETHEREUM':
      return { name: 'Dai Stablecoin', version: '1' }
    case 'USD1-BSC':
      return { name: 'World Liberty Financial USD', version: '1' }
    case 'USDG-ROBINHOOD':
      return { name: 'Global Dollar', version: '1' }
    case 'USDC-PULSECHAIN':
    default:
      return { name: 'USD Coin from Ethereum', version: '1' }
  }
}

// EIP-3009 transferWithAuthorization domain name/version per token. Mirrors
// liberty-swap TransferWithAuthorizationBuilder.getDomainInfo.
function getTransferAuthDomainInfo(tokenId: string): { name: string; version: string } {
  switch (tokenId) {
    case 'USDC-MONAD':
      return { name: 'USDC', version: '2' }
    default:
      return { name: 'USD Coin', version: '2' }
  }
}

// ─────────────────────────────────────────────── typed-data builders ────

const HOUR_SECONDS = 3600

export interface GaslessTokenLike {
  id: string
  address: string
  symbol: string
  decimals: number
  chainId: number
}

/**
 * EIP-2612 permit signature payload. `nonce` must be read on-chain from the
 * token's `nonces(owner)` by the caller (kept out of here so this stays pure).
 */
export function buildPermitTypedData(params: {
  owner: string
  inputToken: GaslessTokenLike
  amount: string
  nonce: bigint | number | string
  deadline?: number
}): GaslessTypedData {
  const spender = assertRelayerConfigured()
  const { owner, inputToken, amount } = params
  const deadline = params.deadline ?? Math.floor(Date.now() / 1000) + HOUR_SECONDS
  const isDai = inputToken.symbol.toUpperCase() === 'DAI'
  const { name, version } = getPermitDomainInfo(inputToken.id)

  const domain: TypedDataDomain = {
    name,
    version,
    chainId: inputToken.chainId,
    verifyingContract: inputToken.address
  }

  if (isDai) {
    return {
      domain,
      types: PERMIT_HOLDER_TYPES,
      primaryType: 'Permit',
      message: {
        holder: owner,
        spender,
        nonce: params.nonce,
        expiry: deadline,
        allowed: true
      }
    }
  }

  const amountInUnits = parseUnits(amount, inputToken.decimals)
  return {
    domain,
    types: PERMIT_OWNER_TYPES,
    primaryType: 'Permit',
    message: {
      owner,
      spender,
      value: amountInUnits.toString(),
      nonce: params.nonce,
      deadline
    }
  }
}

/**
 * EIP-3009 transferWithAuthorization payload (single-signature flow).
 * The `nonce` is a deterministic hash the relayer re-derives to bind the
 * authorization to this exact swap intent.
 */
export function buildTransferAuthTypedData(params: {
  from: string
  inputToken: GaslessTokenLike
  outputToken: GaslessTokenLike
  amount: string
  destinationAddress: string
  correlationId: string
  validBefore?: number
  /** When set, encodes a RAILGUN Fast-shield instead of a normal EVM delivery. */
  railgunShield?: RailgunShieldOverride
}): GaslessTypedData {
  const relayer = assertRelayerConfigured()
  const {
    from,
    inputToken,
    outputToken,
    amount,
    destinationAddress,
    correlationId,
    railgunShield
  } = params
  const router = getRouterOrThrow(inputToken.id)
  const amountInUnits = parseUnits(amount, inputToken.decimals)

  const validAfter = 0
  const validBefore = params.validBefore ?? Math.floor(Date.now() / 1000) + HOUR_SECONDS
  // RAILGUN shield overrides all three swap-routing fields together; otherwise a
  // normal EVM delivery to `destinationAddress` on the output chain.
  const recipient = railgunShield ? railgunShield.encryptedRecipient : destinationAddress
  const recipientType = railgunShield
    ? SwapRecipientType.CUSTOM
    : getSwapRecipientType(destinationAddress)
  const customChainId = railgunShield ? railgunShield.destChainId : outputToken.chainId

  // The relayer re-derives this preimage field for field; `integratorId` sits
  // between `targetToken` and `correlationId`. A reorder still produces a valid
  // signature but for a nonce the relayer never expects, so the burn is dropped.
  const expectedNonce = keccak256(
    AbiCoder.defaultAbiCoder().encode(
      ['address', 'address', 'uint256', 'bytes', 'uint8', 'address', 'bytes32', 'bytes32'],
      [
        inputToken.address,
        router,
        customChainId,
        recipient,
        recipientType,
        outputToken.address,
        getDefaultIntegratorId(inputToken.id),
        correlationId
      ]
    )
  )

  // USDC transferWithAuthorization domain. Circle-native USDC signs as
  // "USD Coin"; the Monad deployment publishes itself as "USDC".
  const { name: domainName, version: domainVersion } = getTransferAuthDomainInfo(inputToken.id)

  return {
    domain: {
      name: domainName,
      version: domainVersion,
      chainId: inputToken.chainId,
      verifyingContract: inputToken.address
    },
    types: TRANSFER_WITH_AUTHORIZATION_TYPES,
    primaryType: 'TransferWithAuthorization',
    message: {
      from,
      to: relayer,
      value: amountInUnits.toString(),
      validAfter,
      validBefore,
      nonce: expectedNonce
    }
  }
}

/**
 * EIP-2612 second signature: LBSGasless SwapParams. Tells the relayer the swap
 * target. `nonce`/`deadline` come from the permit message signed first.
 */
export function buildSwapParamsTypedData(params: {
  from: string
  inputToken: GaslessTokenLike
  outputToken: GaslessTokenLike
  amount: string
  destinationAddress: string
  nonce: number | string
  deadline: number
  correlationId: string
  /** When set, encodes a RAILGUN Fast-shield instead of a normal EVM delivery. */
  railgunShield?: RailgunShieldOverride
}): GaslessTypedData {
  const relayer = assertRelayerConfigured()
  const { from, inputToken, outputToken, amount, destinationAddress, railgunShield } = params
  const router = getRouterOrThrow(inputToken.id)
  const amountInUnits = parseUnits(amount, inputToken.decimals)
  const recipient = railgunShield ? railgunShield.encryptedRecipient : destinationAddress
  const recipientType = railgunShield
    ? SwapRecipientType.CUSTOM
    : getSwapRecipientType(destinationAddress)
  const customChainId = railgunShield ? railgunShield.destChainId : outputToken.chainId

  return {
    domain: {
      name: 'LBSGasless',
      version: '1',
      chainId: inputToken.chainId,
      verifyingContract: relayer
    },
    types: SWAP_PARAMS_TYPES,
    primaryType: 'SwapParams',
    message: {
      user: from,
      token: inputToken.address,
      router,
      amount: amountInUnits.toString(),
      destChainId: customChainId,
      recipient,
      recipientType,
      targetToken: outputToken.address,
      integratorId: getDefaultIntegratorId(inputToken.id),
      nonce: params.nonce,
      deadline: params.deadline,
      correlationId: params.correlationId
    }
  }
}

// Live `/app/routers` endpoint (apis.libertyswap.finance/app). Same source
// LibertySwapAPI.loadBridgeContracts reads; kept here so the UI gasless flow —
// which is decoupled from the swap controller — can guarantee fresh routers
// before signing.
const getGaslessRoutersApiUrl = () => `${getLibertyAppApiBase()}/routers`

let gaslessRoutersLoaded = false
let gaslessRoutersPromise: Promise<void> | null = null

/**
 * Ensure the live `/app/routers` overrides are loaded BEFORE a gasless swap is
 * signed. The router address is baked into both the signed EIP-3009 nonce and
 * the SwapParams/submit payload, so a stale hardcoded router makes the relayer
 * revert on-chain ("execution reverted (unknown custom error)", HTTP 400 from
 * /gasless/api/swap/submit). Mirrors LibertySwapAPI.loadBridgeContracts but is
 * callable from the gasless hook. Best-effort + deduped: on any failure the
 * hardcoded LIBERTY_SWAP_CONTRACTS remain in effect and the next call retries.
 */
export async function ensureGaslessRoutersLoaded(): Promise<void> {
  if (gaslessRoutersLoaded) return
  if (gaslessRoutersPromise) return gaslessRoutersPromise

  gaslessRoutersPromise = (async () => {
    try {
      const response = await fetch(getGaslessRoutersApiUrl(), {
        method: 'GET',
        headers: { accept: 'application/json' }
      })
      if (!response.ok) throw new Error(`/app/routers responded ${response.status}`)

      const json = await response.json()
      // API may return the array directly or wrap it in { data: [...] }.
      const list: unknown = Array.isArray(json) ? json : json?.data
      if (!Array.isArray(list)) throw new Error('Unexpected /app/routers response shape')

      const overrides: Record<string, string> = {}
      // The `version` field rides along with the address: it is what tells the V1
      // and V2 swap ABIs apart, and a redeploy can change both in the same response.
      const versions: Record<string, string> = {}
      for (const item of list) {
        const router: string | undefined = item?.router
        const symbol: string | undefined = item?.symbol
        const version: string | undefined = item?.version
        const chainId = Number(item?.chainId)
        if (!router || !symbol || !chainId) continue
        const token = LIBERTY_TOKENS.find((t) => t.chainId === chainId && t.symbol === symbol)
        if (!token) continue
        overrides[token.id] = router
        // Absent version: leave the token out so shouldUseContractV1 keeps using the
        // hardcoded fallback rather than silently defaulting it to the V2 ABI.
        if (version) versions[token.id] = version
      }

      if (Object.keys(overrides).length > 0) {
        setDynamicSwapContracts(overrides, versions)
        gaslessRoutersLoaded = true
      }
    } catch (error) {
      // Keep the hardcoded fallback; log and allow a retry on the next call.
      console.warn('[Gasless] /app/routers fetch failed, using hardcoded routers:', error)
    } finally {
      gaslessRoutersPromise = null
    }
  })()

  return gaslessRoutersPromise
}

// Router resolution for SIGNED payloads: the live `/app/routers` registry only,
// never the hardcoded map.
//
// The router is baked into the EIP-3009 nonce and the SwapParams signature, and
// the relayer re-derives both from its OWN registry. The static map in
// constants.ts holds production addresses, which differ per environment (dev
// USDC-BASE is 0x0F799DBd…, the static entry is 0xbCE3e2D0…), so falling back to
// it produces a perfectly valid signature that the relayer can never match —
// surfacing as "FiatTokenV2: invalid signature" AFTER the user has signed.
// Failing here instead keeps that failure in front of the signature prompt.
function getRouterOrThrow(tokenId: string): string {
  const router = getLoadedSwapContractAddress(tokenId)
  if (!router)
    throw new Error(
      `No gasless router loaded for ${tokenId} — /app/routers must be loaded ` +
        '(ensureGaslessRoutersLoaded) before signing a gasless swap.'
    )
  return router
}

// ─────────────────────────────────────────────────── submit request ────

export interface SubmitGaslessSwapRequest {
  router: string
  sourceToken: string
  user: string
  amount: string
  correlationId: string
  destChainId: number
  recipient: string
  recipientType: number
  targetToken: string
  sourceChainId: number
  permitType: GaslessPermitType
  validAfterOrDeadline: number
  validBefore: number
  nonce: string
  v: number
  r: string
  s: string
  /** EIP-2612 only — the SwapParams signature. EIP-3009 is single-signature. */
  vSwap?: number
  rSwap?: string
  sSwap?: string
  signature: string
  integratorId: string
}

export interface SubmitGaslessSwapResponse {
  correlationId: string
  txHash?: string
  message: string
  status: string | number
  timestamp: string
}

export interface GaslessSwapStatusStep {
  txHash: string
  status: 'pending' | 'completed' | 'failed'
}

/**
 * The relayer answers in two shapes. The dev backend returns a flat
 * `{ correlationId, status, txHash }` (verified 2026-08-18 against a real swap);
 * the richer per-step shape is what this client was originally written against.
 * Everything past `status` is therefore optional — read the settled hash with
 * `getGaslessSwapTxHash` rather than reaching into `steps` directly.
 */
export interface GetGaslessSwapStatusResponse {
  correlationId: string
  status: string
  /** Flat shape: the settled on-chain swap hash. */
  txHash?: string
  errorMessage?: string
  progress?: number
  message?: string
  steps?: {
    transfer?: GaslessSwapStatusStep
    approval?: GaslessSwapStatusStep
    swap?: GaslessSwapStatusStep
  }
  timestamp?: string
}

/** The settled swap hash, whichever response shape the relayer used. */
export function getGaslessSwapTxHash(res: GetGaslessSwapStatusResponse | null): string | undefined {
  return res?.txHash || res?.steps?.swap?.txHash
}

/**
 * Assemble the relayer submit body from one (EIP-3009) or two (EIP-2612)
 * signatures. `permitMessage` is the message that was signed first.
 */
export function assembleGaslessSubmitRequest(params: {
  permitType: GaslessPermitType
  user: string
  inputToken: GaslessTokenLike
  outputToken: GaslessTokenLike
  amount: string
  destinationAddress: string
  correlationId: string
  permitMessage: Record<string, any>
  signature1: string
  signature2?: string
  /** When set, submits a RAILGUN Fast-shield instead of a normal EVM delivery. */
  railgunShield?: RailgunShieldOverride
}): SubmitGaslessSwapRequest {
  const {
    permitType,
    user,
    inputToken,
    outputToken,
    amount,
    destinationAddress,
    correlationId,
    permitMessage,
    railgunShield
  } = params

  const router = getRouterOrThrow(inputToken.id)
  const sig1 = Signature.from(params.signature1)
  const sig2 = params.signature2 ? Signature.from(params.signature2) : undefined
  const amountInUnits = parseUnits(amount, inputToken.decimals)

  const validAfterOrDeadline = Number(
    permitMessage.validAfter ?? permitMessage.deadline ?? permitMessage.expiry ?? 0
  )
  const validBefore = Number(permitMessage.validBefore ?? 0)
  const nonce = permitType === 'EIP-3009' ? String(permitMessage.nonce ?? ZeroHash) : ZeroHash

  // The canonical 65-byte serialized signature (first signature).
  const signature = Signature.from({ v: sig1.v, r: sig1.r, s: sig1.s }).serialized
  const recipient = railgunShield ? railgunShield.encryptedRecipient : destinationAddress
  const recipientType = railgunShield
    ? SwapRecipientType.CUSTOM
    : getSwapRecipientType(destinationAddress)
  const destChainId = railgunShield ? railgunShield.destChainId : outputToken.chainId

  // vSwap/rSwap/sSwap belong to the SwapParams signature, which only the
  // EIP-2612 flow produces; sending them for EIP-3009 makes the relayer read a
  // second signature that was never signed.
  const swapSignature =
    permitType === 'EIP-2612' && sig2
      ? { vSwap: sig2.v, rSwap: sig2.r, sSwap: sig2.s }
      : { vSwap: undefined, rSwap: undefined, sSwap: undefined }

  return {
    router,
    sourceToken: inputToken.address,
    user,
    amount: amountInUnits.toString(),
    correlationId,
    destChainId,
    recipient,
    recipientType,
    targetToken: outputToken.address,
    sourceChainId: inputToken.chainId,
    permitType,
    validAfterOrDeadline,
    validBefore,
    nonce,
    v: sig1.v,
    r: sig1.r,
    s: sig1.s,
    ...swapSignature,
    signature,
    integratorId: getDefaultIntegratorId(inputToken.id)
  }
}

// ────────────────────────────────────────────────────────── relayer io ────

export async function submitGaslessSwap(
  request: SubmitGaslessSwapRequest
): Promise<SubmitGaslessSwapResponse | null> {
  const res = await fetch(`${getGaslessApiBase()}/api/swap/submit`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  })

  if (!res.ok) {
    // Read the body ONCE as text and keep it. When the relayer simulates the
    // swap and it reverts, `message` is the useless half of the answer
    // ("execution reverted (unknown custom error)") while the half that names
    // the cause - the 4-byte custom-error selector - rides in `data`/`error`.
    // Dropping it (this used to keep only `message`, then swallow the whole
    // error into `null`) left the caller reporting "the relayer did not accept
    // the Fast shield", i.e. no way to tell an unregistered integrator from a
    // bad permit from an unsupported route without opening the Network tab.
    const raw = await res.text().catch(() => '')
    let parsed: any
    try {
      parsed = raw ? JSON.parse(raw) : undefined
    } catch {
      parsed = undefined
    }
    const detail = parsed?.data ?? parsed?.error?.data ?? parsed?.reason ?? parsed?.error
    const detailText = typeof detail === 'string' ? detail : detail && JSON.stringify(detail)

    console.error('[Gasless] submit rejected', res.status, raw || '<empty body>', {
      router: request.router,
      sourceToken: request.sourceToken,
      sourceChainId: request.sourceChainId,
      destChainId: request.destChainId,
      permitType: request.permitType,
      integratorId: request.integratorId,
      correlationId: request.correlationId
    })

    throw new Error(
      [parsed?.message || `Gasless submit failed (${res.status})`, detailText]
        .filter(Boolean)
        .join(' - ')
    )
  }

  return (await res.json()) as SubmitGaslessSwapResponse
}

// The relayer's status route has two shapes in the wild. Verified against the
// dev backend on 2026-08-18 with a real swap: it serves `/swap/status/{id}` and
// 404s `/swap/{id}/status`. Production could not be probed (nginx 403s
// unauthenticated GETs), so the legacy shape stays as a fallback instead of
// being deleted — guessing wrong is invisible at runtime: the poll never
// reaches a terminal status, settlement is never announced, and the
// tx/process report is dropped with it.
// Relative to the gasless base, which already ends in `/gasless` (dev) or
// `/v3/gasless` (uat/prod).
const STATUS_PATHS: ((id: string) => string)[] = [
  (id) => `/api/swap/status/${id}`,
  (id) => `/api/swap/${id}/status`
]

// Latched once a shape answers, so a poll costs one request, not two.
let statusPathIdx = 0

export async function getGaslessSwapStatus(
  correlationId: string
): Promise<GetGaslessSwapStatusResponse | null> {
  for (let i = 0; i < STATUS_PATHS.length; i++) {
    const idx = (statusPathIdx + i) % STATUS_PATHS.length
    const buildPath = STATUS_PATHS[idx]
    if (!buildPath) continue
    const path = buildPath(correlationId)
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(`${getGaslessApiBase()}${path}`, {
        headers: { Accept: 'application/json' }
      })
      // Only a 404 means "wrong shape, try the other one". Any other non-ok is
      // a real error for a path that exists, so don't mask it by retrying.
      if (res.status === 404) continue
      if (!res.ok) {
        console.error('[Gasless] status failed:', res.status, path)
        return null
      }
      statusPathIdx = idx
      // eslint-disable-next-line no-await-in-loop
      return (await res.json()) as GetGaslessSwapStatusResponse
    } catch (e) {
      console.error('[Gasless] status failed:', e)
      return null
    }
  }
  // Every shape 404s. Logged rather than swallowed: this used to fail silently,
  // which stranded the settlement poll until its 5-minute timeout and dropped
  // the tx/process report with it.
  console.error('[Gasless] status: no known endpoint answered for', correlationId)
  return null
}

// Settled-OK vocabulary. `SUCCESS` is what the dev relayer actually returns
// (observed 2026-08-18); the COMPLETED spellings are kept because they are what
// this client was written against and no promise was made that they went away.
// A status missing from BOTH sets is treated as still-running, so an unknown
// spelling costs a 5-minute timeout rather than a wrong outcome — that is the
// safe direction, but it is also why new spellings must be added here.
const GASLESS_SUCCESS_STATUSES = ['SUCCESS', 'SUCCESSFUL', 'COMPLETED', 'SWAP_COMPLETED']
const GASLESS_FAILED_STATUSES = ['FAILED', 'SWAP_FAILED', 'ERROR']

/** True once the relayer reports the swap fully settled or failed. */
export function isGaslessSwapTerminal(status: string): boolean {
  const s = status.toUpperCase()
  return GASLESS_SUCCESS_STATUSES.includes(s) || GASLESS_FAILED_STATUSES.includes(s)
}

export function isGaslessSwapFailed(status: string): boolean {
  return GASLESS_FAILED_STATUSES.includes(status.toUpperCase())
}

// ─────────────────────────────────────────── liberty order tracking (io) ────
// LibertySwap's own frontend records every swap with its order backend after
// broadcast (createOrder → POST /app/orders) and again once the tx confirms
// on-chain (submitTxHash → POST /app/swap/tx/process). Our swap paths
// (gasless relayer / permit2 solver) settle without either call, so those
// swaps never appear in LibertySwap's order history / volume tracking. These
// two best-effort helpers close that gap. A failure here must never surface to
// the user or affect the swap — it has already settled on-chain by the time
// these run. Both endpoints live under the same host as the gasless relayer
// (apis.libertyswap.finance/app), see GASLESS_API_BASE above.

export interface LibertyOrderParams {
  inputTxHash: string
  fromAddress: string
  recipientAddress: string
  srcTokenAddress: string
  srcChainId: number
  /** Human-readable amount string (NOT wei), matching liberty's createOrder. */
  srcTokenAmount: string
  dstChainId: number
  dstTokenAddress: string
}

/** Register a settled swap with LibertySwap's order backend (POST /app/orders). */
export async function notifyLibertyOrder(params: LibertyOrderParams): Promise<void> {
  try {
    const res = await fetch(`${getLibertyAppApiBase()}/orders`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      console.error('[Liberty] create order failed:', err)
    }
  } catch (e) {
    console.error('[Liberty] create order error:', e)
  }
}

/**
 * Notify LibertySwap's backend that a swap's on-chain tx is confirmed so it can
 * index/process the order (POST /app/swap/tx/process). Mirrors liberty-swap's
 * submitTxHash, which it calls after waiting for block confirmation.
 */
export type LibertyTransactionType = 'SWAP_INITIATED' | 'CCTP_INBOUND_BURNED'

export async function submitLibertyTxProcess(
  txHash: string,
  chainId: number,
  // A CCTP burn must be reported as CCTP_INBOUND_BURNED: it is now the only
  // signal the backend gets for that swap (confirm-burn is gone), and the
  // relayer pairs it with the attested burn message.
  transactionType: LibertyTransactionType = 'SWAP_INITIATED'
): Promise<void> {
  try {
    const res = await fetch(`${getLibertyAppApiBase()}/swap/tx/process`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ txHash, chainId, transactionType, target: 'LIBERTY_SWAP' })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      console.error('[Liberty] tx/process failed:', err)
    }
  } catch (e) {
    console.error('[Liberty] tx/process error:', e)
  }
}
